import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiDownload, FiCheckCircle, FiRotateCcw, FiEye, FiEdit2 } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

import DataTable from '../../Components/UI/DataTable';
import Button from '../../Components/UI/Button';
import Modal from '../../Components/UI/Modal';
import DeleteConfirmatationModal from '../../Components/CommonComponents/DeleteConfirmatationModal';
import useTableData from '../../Components/CommonComponents/useTableData';
import { apiGet, apiDelete } from '../../Components/CommonComponents/tableApi';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';
import { downloadOrderPdf } from './orderPdf';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';

/**
 * Orders screen.
 *
 * Rows here are orders, not medicine lines: the server rolls every line up by
 * Order_Id, so one order with ten medicines is one row (and one PDF).
 */

const formatDate = (value) => {
  if (!value) return 'NA';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'NA'
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
      status === 'received' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
    }`}
  >
    <span className={`h-1.5 w-1.5 rounded-full ${status === 'received' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
    {status === 'received' ? 'Received' : 'Pending'}
  </span>
);

const medicineSummary = (items = []) => {
  const names = items.map((item) => item?.nameOfMedicine).filter(Boolean);
  if (names.length === 0) return 'NA';
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
};

const formatMoney = (value) =>
  (Number(value) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Same maths as the server: qty x rate, less discount %, plus GST % on the rest.
const lineAmounts = (item) => {
  if (typeof item?.rate !== 'number') return null;
  const gross = (Number(item.quantity) || 0) * item.rate;
  const discountAmount = (gross * (Number(item.discount) || 0)) / 100;
  const taxable = gross - discountAmount;
  const gstAmount = (taxable * (Number(item.gst) || 0)) / 100;
  return { gross, discountAmount, taxable, gstAmount, total: taxable + gstAmount };
};

const DetailField = ({ label, children }) => (
  <div>
    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
    <dd className="mt-0.5 text-sm text-slate-900">{children || <span className="text-slate-300">—</span>}</dd>
  </div>
);

// Everything about one order: invoice details, every line and the totals.
const OrderDetailModal = ({ order, onClose, onEdit }) => {
  const items = order?.items || [];
  // Every line carries the order-level fields, so the first line is a fallback.
  const details = order && 'invoiceNo' in order ? order : items[0] || {};
  const totals = items.reduce(
    (sum, item) => {
      const amounts = lineAmounts(item);
      if (!amounts) return sum;
      return {
        subtotal: sum.subtotal + amounts.gross,
        discountTotal: sum.discountTotal + amounts.discountAmount,
        gstTotal: sum.gstTotal + amounts.gstAmount,
        netTotal: sum.netTotal + amounts.total,
        pricedItems: sum.pricedItems + 1,
      };
    },
    { subtotal: 0, discountTotal: 0, gstTotal: 0, netTotal: 0, pricedItems: 0 }
  );
  return (
    <Modal
      isOpen={Boolean(order)}
      onClose={onClose}
      title={`Order ${order?.Order_Id || ''}`}
      subtitle={`${formatDate(order?.date)} · ${order?.distributorName || 'NA'}`}
      maxWidth="max-w-5xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button variant="secondary" onClick={() => onEdit(order)}>
            <FiEdit2 className="h-4 w-4" />
            Edit Order
          </Button>
        </>
      }
    >
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <DetailField label="Status">
          <StatusBadge status={order?.status} />
        </DetailField>
        <DetailField label="Invoice No.">{details?.invoiceNo}</DetailField>
        <DetailField label="Invoice Date">{details?.invoiceDate ? formatDate(details.invoiceDate) : ''}</DetailField>
        <DetailField label="Date Order Dispatched">{details?.dispatchDate ? formatDate(details.dispatchDate) : ''}</DetailField>
        <DetailField label="Date of Receipt">{details?.receiptDate ? formatDate(details.receiptDate) : ''}</DetailField>
        <DetailField label="Marked Received On">{order?.receivedOn ? formatDate(order.receivedOn) : ''}</DetailField>
        <div className="col-span-2">
          <DetailField label="Notes">
            {details?.notes ? <span className="whitespace-pre-wrap">{details.notes}</span> : ''}
          </DetailField>
        </div>
      </dl>

      <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Medicine</th>
              <th className="px-3 py-2 text-left">Batch No.</th>
              <th className="px-3 py-2 text-left">HSN No.</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Rate</th>
              <th className="px-3 py-2 text-right">Disc %</th>
              <th className="px-3 py-2 text-right">GST %</th>
              <th className="px-3 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((item) => {
              const amounts = lineAmounts(item);
              return (
                <tr key={item?._id}>
                  <td className="px-3 py-2">
                    <p className="font-medium text-slate-900">{item?.nameOfMedicine}</p>
                    <p className="text-[11px] text-slate-500">
                      {[item?.potencyOrPower, item?.company, item?.packing].filter(Boolean).join(' · ')}
                    </p>
                  </td>
                  <td className="px-3 py-2">{item?.batchNo || '—'}</td>
                  <td className="px-3 py-2">{item?.hsnNo || '—'}</td>
                  <td className="px-3 py-2 text-right">{item?.quantity}</td>
                  <td className="px-3 py-2 text-right">{typeof item?.rate === 'number' ? formatMoney(item.rate) : '—'}</td>
                  <td className="px-3 py-2 text-right">{item?.discount ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{item?.gst ?? '—'}</td>
                  <td className="px-3 py-2 text-right font-medium">{amounts ? formatMoney(amounts.total) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totals.pricedItems > 0 && (
        <dl className="ml-auto mt-4 w-full max-w-xs space-y-1 text-sm">
          <div className="flex justify-between text-slate-600">
            <dt>Subtotal</dt>
            <dd>{formatMoney(totals.subtotal)}</dd>
          </div>
          <div className="flex justify-between text-slate-600">
            <dt>Discount</dt>
            <dd>{totals.discountTotal > 0 ? `-${formatMoney(totals.discountTotal)}` : formatMoney(0)}</dd>
          </div>
          <div className="flex justify-between text-slate-600">
            <dt>GST</dt>
            <dd>+{formatMoney(totals.gstTotal)}</dd>
          </div>
          <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold text-slate-900">
            <dt>Net Total</dt>
            <dd>{formatMoney(totals.netTotal)}</dd>
          </div>
          {totals.pricedItems < items.length && (
            <p className="text-[11px] text-slate-500">Lines without a rate are not included.</p>
          )}
        </dl>
      )}
    </Modal>
  );
};

export default function OrdersTable() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [busyOrderId, setBusyOrderId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);

  const editOrder = (order) => navigate(`/main/inventory/Order/Order/${encodeURIComponent(order?.Order_Id)}`);

  const fetcher = useCallback(
    (query) =>
      apiGet(
        `inventory/orders/grouped?search=${encodeURIComponent(query.search)}&page=${query.page}&limit=${query.limit}${
          statusFilter ? `&status=${statusFilter}` : ''
        }`
      ),
    [statusFilter]
  );

  const table = useTableData(fetcher, [statusFilter]);

  const handleDownload = async (order) => {
    setBusyOrderId(order?.Order_Id);
    const toastId = toast.loading('Preparing PDF...');
    try {
      await downloadOrderPdf(order);
      toast.dismiss(toastId);
      toast.success('Order PDF downloaded');
    } catch (error) {
      toast.dismiss(toastId);
      toast.error('Could not generate the order PDF');
      console.log('Order PDF failed', error);
    } finally {
      setBusyOrderId(null);
    }
  };

  // Receiving flips every line of the order at once.
  const handleStatus = async (order, status) => {
    setBusyOrderId(order?.Order_Id);
    try {
      await axios.put(
        `${END_POINT}/inventory/orders/status/${encodeURIComponent(order?.Order_Id)}`,
        { status },
        { headers: { Authorization: getJwtToken() } }
      );
      toast.success(status === 'received' ? 'Order marked as received' : 'Order moved back to pending');
      table.reload();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not update the order status');
      console.log('Order status update failed', error);
    } finally {
      setBusyOrderId(null);
    }
  };

  // One spreadsheet row per medicine line of every order matching the current
  // search and status filter (not just the visible page).
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const result = await apiGet(
        `inventory/orders/grouped?search=${encodeURIComponent(table.query.search || '')}&page=1&limit=100000${
          statusFilter ? `&status=${statusFilter}` : ''
        }`
      );
      const lines = (result?.data || []).flatMap((order) =>
        (order?.items || []).map((item) => ({
          ...item,
          Order_Id: order.Order_Id,
          date: order.date,
          status: order.status,
          receivedOn: order.receivedOn,
        }))
      );
      if (lines.length === 0) {
        toast.info('No orders to export');
        return;
      }
      exportToExcel(
        lines,
        'Orders',
        [
          { key: 'Order_Id', label: 'Order Id' },
          { key: 'date', label: 'Date', format: (v) => formatDate(v) },
          { key: 'status', label: 'Status' },
          { key: 'receivedOn', label: 'Received On', format: (v) => (v ? formatDate(v) : '') },
          { key: 'invoiceNo', label: 'Invoice No.', format: (v) => v || '' },
          { key: 'invoiceDate', label: 'Invoice Date', format: (v) => (v ? formatDate(v) : '') },
          { key: 'dispatchDate', label: 'Date Order Dispatched', format: (v) => (v ? formatDate(v) : '') },
          { key: 'receiptDate', label: 'Date of Receipt', format: (v) => (v ? formatDate(v) : '') },
          { key: 'distributorName', label: 'Distributor' },
          { key: 'nameOfMedicine', label: 'Medicine' },
          { key: 'company', label: 'Company' },
          { key: 'potencyOrPower', label: 'Potency' },
          { key: 'typeOfMedicine', label: 'Type' },
          { key: 'packing', label: 'Packing' },
          { key: 'quantity', label: 'Quantity' },
          { key: 'rate', label: 'Rate', format: (v) => (v ?? '') },
          { key: 'amount', label: 'Amount', format: (v, row) => (typeof row?.rate === 'number' ? Number(row.quantity || 0) * row.rate : '') },
          { key: 'discount', label: 'Discount %', format: (v) => (v ?? '') },
          { key: 'gst', label: 'GST %', format: (v) => (v ?? '') },
          { key: 'netAmount', label: 'Net Amount', format: (v, row) => { const a = lineAmounts(row); return a ? Math.round(a.total * 100) / 100 : ''; } },
          { key: 'batchNo', label: 'Batch No.', format: (v) => v || '' },
          { key: 'hsnNo', label: 'HSN No.', format: (v) => v || '' },
          { key: 'expiryDate', label: 'Expiry Date', format: (v) => (v ? formatDate(v) : '') },
          { key: 'notes', label: 'Order Notes', format: (v) => v || '' },
        ],
        'Orders'
      );
    } finally {
      setIsExporting(false);
    }
  };

  const headers = [
    { key: 'Order_Id', label: 'Order Id', render: (value) => <span className="font-medium text-slate-900">{value}</span> },
    { key: 'date', label: 'Date', render: (value) => formatDate(value) },
    { key: 'distributorName', label: 'Distributor' },
    { key: 'items', label: 'Medicines', render: (value) => medicineSummary(value) },
    { key: 'itemCount', label: 'Items', align: 'right' },
    { key: 'totalQuantity', label: 'Total Qty', align: 'right' },
    { key: 'invoiceNo', label: 'Invoice No.' },
    {
      key: 'netTotal',
      label: 'Net Total',
      align: 'right',
      render: (value, row) => (row?.pricedItems > 0 ? formatMoney(value) : ''),
    },
    { key: 'status', label: 'Status', render: (value) => <StatusBadge status={value} /> },
  ];

  return (
    <div className="w-full">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">Orders</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" isLoading={isExporting} onClick={handleExport}>
            <FiDownload className="h-4 w-4" />
            Export to Excel
          </Button>
          <Button variant="secondary" onClick={() => navigate('/main/inventory/Order/Order/addNew')}>
            + Add Order
          </Button>
        </div>
      </div>

      <DataTable
        headers={headers}
        data={table.rows}
        isLoading={table.isLoading}
        emptyMessage="No orders yet."
        page={table.query.page}
        totalPages={table.totalPages}
        rowsPerPage={table.query.limit}
        onPageChange={table.setPage}
        onRowsPerPageChange={table.setLimit}
        onSearch={table.setSearch}
        searchValue={table.query.search}
        searchPlaceholder="Search by order id, medicine or distributor"
        filters={[
          {
            key: 'status',
            label: 'All Statuses',
            value: statusFilter,
            options: [
              { value: 'pending', label: 'Pending' },
              { value: 'received', label: 'Received' },
            ],
          },
        ]}
        onFilter={(key, value) => setStatusFilter(value)}
        actions={(order) => (
          <>
            <button
              type="button"
              onClick={() => setViewOrder(order)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              <FiEye className="h-3.5 w-3.5" />
              View
            </button>

            <button
              type="button"
              onClick={() => editOrder(order)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
            >
              <FiEdit2 className="h-3.5 w-3.5" />
              Edit
            </button>

            <button
              type="button"
              disabled={busyOrderId === order?.Order_Id}
              onClick={() => handleDownload(order)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            >
              <FiDownload className="h-3.5 w-3.5" />
              PDF
            </button>

            {order?.status === 'received' ? (
              <button
                type="button"
                disabled={busyOrderId === order?.Order_Id}
                onClick={() => handleStatus(order, 'pending')}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-50"
              >
                <FiRotateCcw className="h-3.5 w-3.5" />
                Undo
              </button>
            ) : (
              <button
                type="button"
                disabled={busyOrderId === order?.Order_Id}
                onClick={() => handleStatus(order, 'received')}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
              >
                <FiCheckCircle className="h-3.5 w-3.5" />
                Received
              </button>
            )}

            <DeleteConfirmatationModal
              deleteFunction={() =>
                apiDelete(`inventory/orders/group/${encodeURIComponent(order?.Order_Id)}`, 'Order', {
                  onDone: table.reload,
                })
              }
              text={`order ${order?.Order_Id}`}
              heading="Delete Order"
            />
          </>
        )}
      />

      <OrderDetailModal order={viewOrder} onClose={() => setViewOrder(null)} onEdit={editOrder} />

      <ToastContainer />
    </div>
  );
}
